import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '../types';

export const AUTH_STORAGE_KEY = 'homehub-auth';

/** Admin account gates: while set, the admin may only use the security setup screen. */
export interface AdminSecurityFlags {
  mustChangePassword: boolean;
  mustEnrollTotp: boolean;
  /** Known from the login flow: login went through the TOTP step ⇔ TOTP is enabled. */
  totpEnabled: boolean;
}

interface AuthState extends AdminSecurityFlags {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (accessToken: string, refreshToken: string, user: AuthUser, flags?: Partial<AdminSecurityFlags>) => void;
  setSecurityFlags: (flags: Partial<AdminSecurityFlags>) => void;
  logout: () => void;
  isAdmin: () => boolean;
  isPartnerDev: () => boolean;
}

const defaultFlags: AdminSecurityFlags = {
  mustChangePassword: false,
  mustEnrollTotp: false,
  totpEnabled: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      ...defaultFlags,
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      login: (accessToken, refreshToken, user, flags) =>
        set({ accessToken, refreshToken, user, ...defaultFlags, ...flags }),
      setSecurityFlags: (flags) => set(flags),
      logout: () => set({ accessToken: null, refreshToken: null, user: null, ...defaultFlags }),
      isAdmin: () => get().user?.role === 'platform_admin' || get().user?.role === 'admin',
      isPartnerDev: () => get().user?.role === 'partner_dev',
    }),
    { name: AUTH_STORAGE_KEY },
  ),
);

/** Reads the persisted auth state straight from localStorage (it may have been written by another tab). */
export function readPersistedAuth(): { accessToken: string | null; refreshToken: string | null } | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw)?.state;
    return state ? { accessToken: state.accessToken ?? null, refreshToken: state.refreshToken ?? null } : null;
  } catch {
    return null;
  }
}

// Keep tabs in sync: rotated admin tokens, logout, etc. written by another tab.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_STORAGE_KEY) {
      void useAuthStore.persist.rehydrate();
    }
  });
}
